import authService from './authService';
import { Holiday, HolidayFormData, HolidaySearchModel } from '../types/holiday';
import { PaginatedResponse, ApiResponse } from '../types/common';

class HolidayService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * Lista feriados com paginação
   * GET /api/holidays
   */
  async getAll(searchModel: HolidaySearchModel): Promise<PaginatedResponse<Holiday>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
      ...(searchModel.orderBy && { order_by: searchModel.orderBy }),
      ...(searchModel.orderDirection && { order_direction: searchModel.orderDirection }),
      ...(searchModel.year != null && { year: searchModel.year.toString() }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/holidays?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar feriados');
    }

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  /**
   * Lista os anos distintos que possuem feriados (mais recente primeiro)
   * GET /api/holidays/years
   */
  async getYears(): Promise<number[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/holidays/years');

    if (!response.ok) {
      throw new Error('Falha ao buscar anos de feriados');
    }

    const json = await response.json();
    return Array.isArray(json.data) ? json.data : [];
  }

  /**
   * Busca um feriado por ID
   * GET /api/holidays/:id
   */
  async getById(id: number): Promise<Holiday> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/holidays/${id}`);

    if (!response.ok) {
      if (response.status === 404) throw new Error('Feriado não encontrado');
      throw new Error('Falha ao buscar feriado');
    }

    const data: ApiResponse<Holiday> = await response.json();
    return data.data!;
  }

  /**
   * Cria um novo feriado
   * POST /api/holidays
   */
  async create(formData: HolidayFormData): Promise<Holiday> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/holidays', formData);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Falha ao criar feriado');
    }

    const data: ApiResponse<Holiday> = await response.json();
    return data.data!;
  }

  /**
   * Atualiza um feriado
   * PUT /api/holidays/:id
   */
  async update(id: number, formData: HolidayFormData): Promise<Holiday> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/holidays/${id}`, formData);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Falha ao atualizar feriado');
    }

    const data: ApiResponse<Holiday> = await response.json();
    return data.data!;
  }

  /**
   * Exclui um feriado
   * DELETE /api/holidays/:id
   */
  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/holidays/${id}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Falha ao excluir feriado');
    }
  }
}

export const holidayService = new HolidayService();
export default holidayService;
